import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle, ShieldCheck, ShieldX, Trash2, UserCog } from "lucide-react";
import { config } from "../../../config";

const apiUrl = config.apiUrl;
const roles = ["user", "student", "client", "admin", "affiliates"];

const ViewUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const usersPerPage = 10;

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const fetchUsers = async (page = currentPage) => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(`${apiUrl}/users`, {
        headers: authHeaders(),
        params: { page, per_page: usersPerPage },
      });

      setUsers(response.data.data || []);
      setTotalPages(response.data.last_page || 1);
      setTotalUsers(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const toggleApproval = async (user) => {
    setSavingId(user.id);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post(
        `${apiUrl}/users/${user.id}/toggle-approval`,
        {},
        { headers: authHeaders() }
      );

      setUsers((prev) =>
        prev.map((item) => (item.id === user.id ? response.data.user : item))
      );
      setSuccess(`${user.name} is now ${response.data.user.approved ? "approved" : "unapproved"}.`);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update approval status.");
    } finally {
      setSavingId(null);
    }
  };

  const updateRole = async (user, role) => {
    setSavingId(user.id);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post(
        `${apiUrl}/users/${user.id}/update-role`,
        { role },
        { headers: authHeaders() }
      );

      setUsers((prev) =>
        prev.map((item) => (item.id === user.id ? response.data.user : item))
      );
      setSuccess(`${user.name}'s role updated.`);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update role.");
    } finally {
      setSavingId(null);
    }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Delete ${user.name}?`)) return;

    setSavingId(user.id);
    setError("");
    setSuccess("");

    try {
      await axios.delete(`${apiUrl}/users/${user.id}`, {
        headers: authHeaders(),
      });
      setSuccess("User deleted successfully.");
      fetchUsers(currentPage);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete user.");
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
          <p className="text-sm text-gray-600">
            Showing {users.length} of {totalUsers} users. Approve users before they can login.
          </p>
        </div>
        <button
          onClick={() => fetchUsers(currentPage)}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="flex items-center rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <CheckCircle className="mr-2 h-4 w-4" />
          {success}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">User</th>
              <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Phone</th>
              <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Role</th>
              <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Approval</th>
              <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Joined</th>
              <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-5 py-4">
                  <div className="font-semibold text-gray-900">{user.name}</div>
                  <div className="text-sm text-gray-500">{user.email || "No email"}</div>
                </td>
                <td className="px-5 py-4 text-sm text-gray-700">{user.phone}</td>
                <td className="px-5 py-4">
                  <select
                    value={user.role || "user"}
                    disabled={savingId === user.id}
                    onChange={(event) => updateRole(user, event.target.value)}
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm capitalize"
                  >
                    {roles.map((role) => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </td>
                <td className="px-5 py-4">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                      user.approved ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {user.approved ? "Approved" : "Pending"}
                  </span>
                </td>
                <td className="px-5 py-4 text-sm text-gray-500">
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}
                </td>
                <td className="px-5 py-4">
                  <div className="flex flex-wrap gap-2">
                    <button
                      disabled={savingId === user.id}
                      onClick={() => toggleApproval(user)}
                      className={`inline-flex items-center rounded-md px-3 py-2 text-xs font-semibold disabled:opacity-60 ${
                        user.approved
                          ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                          : "bg-green-100 text-green-800 hover:bg-green-200"
                      }`}
                    >
                      {user.approved ? <ShieldX className="mr-1 h-4 w-4" /> : <ShieldCheck className="mr-1 h-4 w-4" />}
                      {user.approved ? "Unapprove" : "Approve"}
                    </button>
                    <button
                      disabled={savingId === user.id}
                      onClick={() => updateRole(user, user.role === "admin" ? "user" : "admin")}
                      className="inline-flex items-center rounded-md bg-blue-100 px-3 py-2 text-xs font-semibold text-blue-800 hover:bg-blue-200 disabled:opacity-60"
                    >
                      <UserCog className="mr-1 h-4 w-4" />
                      {user.role === "admin" ? "Make User" : "Make Admin"}
                    </button>
                    <button
                      disabled={savingId === user.id}
                      onClick={() => deleteUser(user)}
                      className="inline-flex items-center rounded-md bg-red-100 px-3 py-2 text-xs font-semibold text-red-800 hover:bg-red-200 disabled:opacity-60"
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!users.length && (
          <div className="p-8 text-center text-gray-500">No users found.</div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewUsers;
